/**
 * Firebase Analytics module.
 * Handles initialization and management of Firebase Analytics.
 *
 * @module firebase/analytics
 */

import {
  getAnalytics,
  setAnalyticsCollectionEnabled,
  isSupported,
} from "firebase/analytics";

/**
 * Analytics instance
 * @type {import('firebase/analytics').Analytics|null}
 */
let analytics = null;

/**
 * Initializes Firebase Analytics if supported by the browser
 * and respects user consent for analytics collection.
 *
 * @async
 * @param {import('firebase/app').FirebaseApp} app - Firebase app instance
 * @returns {Promise<import('firebase/analytics').Analytics|null>} Analytics instance or null if not supported
 */
export const initializeAnalytics = async (app) => {
  try {
    const analyticsSupported = await isSupported();

    if (analyticsSupported) {
      analytics = getAnalytics(app);

      const hasAnalyticsConsent = document.cookie.split(";").some((c) => {
        const cookie = c.trim();
        return cookie.startsWith("analytics=true");
      });

      window.cookieConsentAnalyticsDisabled = !hasAnalyticsConsent;

      window["ga-disable-G-ZZ8GCQ27TZ"] = !hasAnalyticsConsent;

      setAnalyticsCollectionEnabled(analytics, hasAnalyticsConsent);
    } else {
      window.analyticsNotSupported = true;
      window.cookieConsentAnalyticsDisabled = true;
      window["ga-disable-G-ZZ8GCQ27TZ"] = true;
    }

    return analytics;
  } catch (error) {
    console.error("Failed to initialize analytics:", error);
    analytics = null;
    return null;
  }
};

/**
 * Updates analytics collection settings based on user consent
 *
 * @param {boolean} isEnabled - Whether analytics should be enabled
 * @returns {boolean} Success status
 */
export const updateAnalyticsState = (isEnabled) => {
  try {
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, isEnabled);
      window.cookieConsentAnalyticsDisabled = !isEnabled;
      window["ga-disable-G-ZZ8GCQ27TZ"] = !isEnabled;
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to update analytics state:", error);
    return false;
  }
};

export { analytics };
